<!-- Figma 28506:20736: title, drop zone and samples on the left; "Preview first" on the right. -->
<div class="upload-layout">
  <section class="upload-main" aria-label="Choose a model">
    <h1 class="flow-title">Give your model a stage.</h1>
    <p class="flow-subtitle">Start with an FBX or USDZ. Your first look is free; both finished videos are ${{ price }} if you choose to buy them.</p>

    <app-render-drop-zone
      mode="inline"
      [invite]="inviteName"
      [sampleDrag]="!!draggingSample"
      [upload]="upload"
      [unpacking]="unpacking"
      [choice]="modelChoice"
      [refused]="!!problem"
      [hint]="zoneHint"
      [helpLink]="true"
      (helpRequested)="openGuide()"
      (filesCollected)="onFiles($event)"
      (sampleDropped)="onSampleDropped()"
      (uploadCancelled)="cancelUpload()"
      (unpackCancelled)="cancelUnpacking()"
      (choiceSelected)="selectModel($event)"
      (choiceConfirmed)="chooseModel()"
      (choiceDismissed)="dismissChoice()">
    </app-render-drop-zone>

    <div class="upload-feedback" [class.is-docked]="!!problem" aria-live="polite">
      <!-- While an archive unpacks: leaving drops it (the designer's line for the upload, Figma
           28701:20500; the upload itself now plays on the processing page). The tab close and
           Back ask as well (beforeunload, RenderUploadLeaveGuard). -->
      <p *ngIf="uploadInFlight" class="upload-leave-note">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><circle cx="9" cy="9" r="8.25" stroke="currentColor" stroke-width="1.5"/><path d="M9 8v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><circle cx="9" cy="5.3" r="1" fill="currentColor"/></svg>
        <span>Keep this page open until the archive is unpacked. Leaving stops it.</span>
      </p>
      <!-- Refused: docked to the zone's red edge, with the button that starts the fix — and, the
           moment something is wrong with the model, the export guide (owner, 2026-10-06; it took
           the place of the header's "?"). Not for a dropped connection: the model is fine. -->
      <div *ngIf="problem" class="upload-refusal" role="alert">
        <div class="upload-refusal__body">
          <p class="upload-refusal__text"><strong>{{ problem.title }}</strong>{{ problem.text }}</p>
          <button *ngIf="problem.code !== 'upload-failed'" class="upload-refusal__guide" type="button" (click)="openGuide()">How to export a model with its textures</button>
        </div>
        <button class="upload-refusal__fix" type="button" (click)="fixProblem()">{{ problem.fix === 'folder' ? 'Choose a folder' : 'Choose a file' }}</button>
      </div>
      <!-- An FBX that asks for textures the drop does not have (render-fbx-textures.ts): a warning —
           the upload may go on without them. Found ones are listed too, so the check reads as real. -->
      <div *ngIf="textureGap as gap" class="texture-gap" role="alert">
        <div class="texture-gap__head">
          <p class="texture-gap__title">{{ gapTitle }}</p>
          <div class="texture-gap__actions">
            <button class="rf-button rf-button--small texture-gap__add" type="button" (click)="textureInput.click()">{{ gap.missing.length === 1 ? 'Add ' + gap.missing[0] : 'Add textures' }}</button>
            <button class="rf-text-link" type="button" (click)="uploadAnyway()">Upload without {{ gap.missing.length === 1 ? 'it' : 'them' }}</button>
          </div>
        </div>
        <!-- Every texture, in a box of six and a half rows that scrolls (designer, 2026-10-16: "and
             9 more" hid the very names to look for). The cut row, the fade and "↓ 9 more" say the
             list goes on — macOS hides the scrollbar. -->
        <div class="texture-gap__box" [class.has-more]="gapBelow > 0">
          <div #gapScroll class="texture-gap__scroll" tabindex="0" role="region" [attr.aria-label]="gapRows.length + ' textures'" (scroll)="measureGapList()">
            <ul #gapList class="texture-gap__list">
              <li *ngFor="let row of gapRows" class="texture-gap__row" [class.is-missing]="row.state === 'missing'">
                <svg *ngIf="row.state !== 'missing'; else missingMark" class="texture-gap__mark" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 8.6 6 12.4 14 3.8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                <ng-template #missingMark><span class="texture-gap__mark texture-gap__dot" aria-hidden="true"></span></ng-template>
                <span class="texture-gap__name" [title]="row.name">{{ row.name }}</span>
                <span class="texture-gap__state" [class.is-added]="row.state === 'added'">{{ row.state }}</span>
              </li>
            </ul>
          </div>
          <button *ngIf="gapBelow > 0" class="texture-gap__more" type="button" tabindex="-1" aria-hidden="true" (click)="scrollGapList()">↓ {{ gapBelow }} more</button>
        </div>
        <p class="texture-gap__note">{{ gapConsequence }}</p>
        <!-- How not to meet this again, in the program the FBX came from (its header names it). -->
        <p class="texture-gap__how">
          <span *ngIf="gapExporter as exporter" class="texture-gap__app">{{ exporter.label }}</span>
          <span class="texture-gap__hint">Next time, {{ gapPackHint }}</span>
          <button class="texture-gap__guide" type="button" (click)="showPackGuide()">Show me how</button>
        </p>
        <input #textureInput type="file" multiple hidden [accept]="textureAccept" (change)="onTexturesPicked($event)">
      </div>
    </div>

    <!-- The samples in full until the user's first model of their own; after it, the heading alone,
         opening them in place (owner, 2026-10-16) — the same words and size folded or open
         (designer, 2026-10-16), and away while a model is on its way in (designer, 2026-10-16).
         Nothing until the list of models is known, so the block does not flash in and fold away. -->
    <div *ngIf="samplesShown !== null" class="sample-block" [class.is-away]="busy || uploadInFlight">
      <div class="sample-row">
        <h2 class="sample-heading" [class.is-folded]="!samplesShown">
          <button *ngIf="samplesFoldable; else sampleTitle" class="sample-toggle" type="button" [attr.aria-expanded]="samplesShown" aria-controls="render-sample-list" (click)="toggleSamples()">
            Or walk through with a sample
            <svg class="sample-toggle__chevron" width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true"><path d="m1 1.5 5 5 5-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <ng-template #sampleTitle>Or walk through with a sample</ng-template>
        </h2>
        <!-- The way to the library besides the profile menu, where not everyone looks (owner,
             2026-10-20): as many models as its page shows, and the profile button's dot while a
             result there is not opened yet. Only once there is something; on a phone, under the
             heading. -->
        <a *ngIf="library$ | async as library" class="library-link" routerLink="/render/library" [attr.aria-label]="library.label">
          <span class="library-link__text">Your library</span>
          <span class="library-link__count">· {{ library.count }} {{ library.count === 1 ? 'model' : 'models' }}</span>
          <span *ngIf="library.ready" class="library-link__dot" [class.is-failed]="library.ready.failed" aria-hidden="true"></span>
          <svg class="library-link__arrow" width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true"><path d="m1.5 1 5 5-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </a>
      </div>
      <!-- Dragged into the zone like files (render-drag.ts); divs, as Firefox will not drag a <button>. -->
      <div *ngIf="samplesShown" id="render-sample-list" class="sample-list">
        <div *ngFor="let sample of samples; trackBy: trackBySampleId" class="sample-item" [class.has-hint]="hint?.sampleId === sample.id">
          <div
            class="sample-button"
            role="button"
            [attr.tabindex]="busy ? -1 : 0"
            [attr.aria-disabled]="busy || null"
            [attr.draggable]="!busy"
            [attr.aria-label]="'Use the ' + sample.name + ' sample'"
            [attr.aria-describedby]="hint?.sampleId === sample.id ? 'render-sample-hint' : null"
            [class.is-dragged]="draggingSample?.id === sample.id"
            [class.is-hinted]="hint?.sampleId === sample.id"
            [class.is-disabled]="busy"
            (pointerdown)="onSamplePointerDown($event)"
            (click)="onSampleActivate(sample, $event, thumb)"
            (keydown.enter)="onSampleActivate(sample, $event, thumb)"
            (keydown.space)="$event.preventDefault(); onSampleActivate(sample, $event, thumb)"
            (dragstart)="onSampleDragStart(sample, $event)"
            (dragend)="onSampleDragEnd(sample)">
            <img #thumb [src]="sample.stills.landscapeCard" alt="" width="56" height="56" draggable="false">
            <span class="sample-name--full">{{ sample.fileName }}</span>
            <span class="sample-name--short" aria-hidden="true">{{ shortName(sample.fileName) }}</span>
          </div>
          <div *ngIf="hint?.sampleId === sample.id" id="render-sample-hint" class="sample-hint" role="status">
            <p *ngIf="!hint?.missed"><strong>Drag and drop it into the drop zone above.</strong> That’s how your own model goes in.</p>
            <p *ngIf="hint?.missed"><strong>Almost there.</strong> Let go inside the dashed box.</p>
            <button class="sample-hint__skip" type="button" (click)="dropForUser(sample, thumb)">Do it for me</button>
          </div>
        </div>
      </div>
    </div>
    <p class="upload-note" *ngIf="!isSimulator">Local file selection demonstrates validation only. Samples let you try the complete flow without uploading anything.</p>
  </section>

  <aside class="side-note">
    <h2>Preview first.<br>Decide after.</h2>
    <p>Check your model, materials, and framing before making a purchase.</p>
    <ul class="check-list">
      <li *ngFor="let benefit of benefits">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1 8.94753L5.2 13.1475L15 3.34753" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        {{ benefit }}
      </li>
    </ul>
    <div class="mini-price">
      <p class="mini-price__line"><strong>${{ price }}</strong>for both finished videos</p>
      <p class="mini-price__note">One model. One payment</p>
    </div>
  </aside>
</div>
